import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Filter, ChevronDown } from 'lucide-react';

const SubcategoryAnalysis = ({ data, fullData }) => {
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  
  // Extract unique categories from the full raw data
  const uniqueCategories = React.useMemo(() => {
    if (!fullData?.data || !Array.isArray(fullData.data)) return [];
    
    const categorySet = new Set();
    fullData.data.forEach(item => {
      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          categorySet.add(cat.category);
        });
      }
    });
    
    return Array.from(categorySet).sort();
  }, [fullData]);

  // Set first category as default
  const [categoryFilter, setCategoryFilter] = useState(uniqueCategories[0] || '');
  
  // Update categoryFilter when categories are loaded
  React.useEffect(() => {
    if (uniqueCategories.length > 0 && !categoryFilter) {
      setCategoryFilter(uniqueCategories[0]);
    }
  }, [uniqueCategories, categoryFilter]);
  
  // Assign colors from a palette
  const colorPalette = [
    '#4f46e5', // indigo
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#f97316', // orange
    '#0ea5e9', // light blue
    '#22c55e', // green
    '#f43f5e', // rose
    '#06b6d4', // cyan
    '#eab308', // yellow
    '#6366f1', // indigo (lighter)
    '#94a3b8', // gray
  ];
  const categoryColorMap = uniqueCategories.reduce((acc, cat, idx) => {
    acc[cat] = colorPalette[idx % colorPalette.length];
    return acc;
  }, {});

  // Process data - show all subcategories for selected category
  const getFilteredData = () => {
    if (!data || !Array.isArray(data) || !categoryFilter) return [];
    
    // Filter by selected category - show all subcategories for this category (excluding "Others")
    let filteredData = data.filter(item => {
      const isCorrectCategory = item.name.startsWith(categoryFilter + ':');
      const [, subcategory] = item.name.split(':');
      const isNotOthers = subcategory && subcategory !== 'Others';
      return isCorrectCategory && isNotOthers;
    });
    
    // Filter by sentiment - show sentiment-specific counts or total
    if (sentimentFilter === 'all') {
      return filteredData.map(item => ({
        ...item,
        value: item.value // Use existing total value
      })).filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);
    } else {
      return filteredData.map(item => ({
        ...item,
        value: item[sentimentFilter] || 0
      })).filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);
    }
  };
  
  const filteredData = getFilteredData();
  
  // Format data for the chart
  const formattedData = filteredData.map(item => {
    const [category, subcategory] = item.name.split(':');
    // Clean up subcategory name for display
    const truncated = subcategory.replace(/_/g, ' ');
    const truncatedLabel = truncated.length > 18 ? truncated.substring(0, 18) + '…' : truncated;
    return {
      ...item,
      formattedName: truncatedLabel,
      fullName: truncated, // for tooltip
      categoryDisplay: category.replace(/_/g, ' '),
      color: categoryColorMap[category] || '#94a3b8',
    };
  });
  
  // Sentiment colors - consistent with other components
  const SENTIMENT_COLORS = {
    positive: '#10b981', // Green
    neutral: '#9ca3af', // Gray
    negative: '#ef4444', // Red
  };
  
  // Calculate dynamic Y-axis width based on label length
  const maxLabelLength = formattedData.length > 0 ? Math.max(...formattedData.map(d => d.fullName.length)) : 100;
  const yAxisWidth = maxLabelLength <= 12 ? 100 : 140;
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="text-sm font-medium">{data.fullName}</p>
          <p className="text-xs text-gray-600">Category: {data.categoryDisplay}</p>
          <p className="text-sm font-semibold mt-1">
            {sentimentFilter === 'all' ? `${data.value} total mentions` : `${data.value} ${sentimentFilter} mentions`}
          </p>
          {sentimentFilter === 'all' && (
            <div className="text-xs mt-2 pt-1 border-t border-gray-100">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: SENTIMENT_COLORS.positive }}></div>
                <span>Positive: {data.positive || 0}</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: SENTIMENT_COLORS.neutral }}></div>
                <span>Neutral: {data.neutral || 0}</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: SENTIMENT_COLORS.negative }}></div>
                <span>Negative: {data.negative || 0}</span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Get sentiment filter label
  const getSentimentFilterLabel = () => {
    switch (sentimentFilter) {
      case 'positive': return 'Positive Mentions';
      case 'negative': return 'Negative Mentions';
      case 'neutral': return 'Neutral Mentions';
      default: return 'All Mentions';
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"
    >
      <div className="mb-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Subcategories Analysis</h3>
          <p className="text-sm text-gray-500">
            All subcategories in {categoryFilter.replace('_', ' ')} - {getSentimentFilterLabel().toLowerCase()}
          </p>
        </div>
        
        {/* Filters moved to new line */}
        <div className="flex items-center space-x-4">
          {/* Category Filter Dropdown */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-0"
            >
              {uniqueCategories.map(category => (
                <option key={category} value={category}>
                  {category.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          
          {/* Sentiment Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Sentiment:</label>
            <div className="relative">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex items-center space-x-1 px-2 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Filter className="w-3 h-3" />
                <span>{getSentimentFilterLabel()}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              
              {showFilterMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                  <div className="py-1">
                    {[
                      { value: 'all', label: 'All Mentions' },
                      { value: 'positive', label: 'Positive Mentions' },
                      { value: 'negative', label: 'Negative Mentions' },
                      { value: 'neutral', label: 'Neutral Mentions' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSentimentFilter(option.value);
                          setShowFilterMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 ${
                          sentimentFilter === option.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={true} stroke="#d1d5db" strokeWidth={1} />
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              dataKey="formattedName"
              type="category"
              axisLine={false}
              tickLine={false}
              width={yAxisWidth}
              tick={{ fontSize: 11, fill: '#6b7280' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              radius={[0, 4, 4, 0]}
              barSize={20}
            >
              {formattedData.map((entry, index) => {
                // Use sentiment color when filtering by sentiment, otherwise use category color
                const barColor = sentimentFilter !== 'all' ? SENTIMENT_COLORS[sentimentFilter] : entry.color;
                return (
                  <Cell key={`cell-${index}`} fill={barColor} />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Only show color legend when filtering by sentiment */}
      {sentimentFilter !== 'all' && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm font-medium">Sentiment Color:</div>
            <div className="text-xs text-gray-500">
              Showing {sentimentFilter} mentions only
            </div>
          </div>
          <div className="flex items-center">
            <div 
              className="w-3 h-3 rounded-full mr-1.5"
              style={{ backgroundColor: SENTIMENT_COLORS[sentimentFilter] }}
            ></div>
            <div className="text-xs font-medium">{sentimentFilter.charAt(0).toUpperCase() + sentimentFilter.slice(1)} Mentions</div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SubcategoryAnalysis;